import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Link,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import Modal from 'react-modal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import OrderByOptionService from '../../../setting/orderByOption/OrderByOptionService';

const PriceNegotiationModal = ({
  isOpen,
  onClose,
  item,
  onComplete,
  style
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  // "เสร็จสิ้น" (accepted) = the admin has sent the ask to the seller — NOT that
  // the seller agreed: the seller answers by lowering the listing, which the
  // price watchers report to the customer. It asks once so a stray tap can't
  // drop the case from the board before the seller was actually messaged.
  const [confirmAccept, setConfirmAccept] = useState(false);
  // Which buyer account (คนกด) the comment was posted from: the dispatch bot sends the
  // order to that buyer only when it is keyed in. Only buyers the bot can reach are listed.
  const [buyers, setBuyers] = useState([]);
  const [buyerId, setBuyerId] = useState('');

  useEffect(() => {
    setConfirmAccept(false);
    setBuyerId('');
  }, [isOpen, item?.negotiationId]);

  useEffect(() => {
    if (!isOpen) return;
    OrderByOptionService.getActiveOrderByOptions()
      .then((res) => setBuyers((res.data || []).filter((o) => o.dispatchEnabled)))
      .catch(() => setBuyers([]));
  }, [isOpen]);

  // The ask against the listing: "¥9,300 off ¥10,000 = 7% (cap 7%)".
  const currentPrice = Number(item?.currentPrice);
  const proposed = Number(item?.proposedPrice);
  const discountPercent =
    currentPrice > 0 && proposed > 0
      ? Math.round(((currentPrice - proposed) / currentPrice) * 1000) / 10
      : null;
  const overCap =
    discountPercent != null && item?.maxDiscountPercent != null && discountPercent > item.maxDiscountPercent;

  // The comment the admin posts on the listing: "could you lower it TO ¥proposed".
  const sellerMessage = proposed > 0
    ? `コメント失礼いたします。\n${proposed.toLocaleString()}円までお値下げしていただくことは可能でしょうか？\n可能でしたら、価格を変更していただけますと幸いです。\nよろしくお願いいたします。`
    : null;

  const handleCopyPrice = () => {
    if (sellerMessage) {
      navigator.clipboard.writeText(sellerMessage)
        .then(() => {
          toast.success('คัดลอกแล้ว');
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
          toast.error('คัดลอกไม่สำเร็จ');
        });
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      await onComplete({
        negotiationId: item.negotiationId,
        status: 'accepted',
        ...(buyerId ? { orderByOptionId: buyerId } : {}),
      });
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason) {
      toast.error('กรุณาเลือกเหตุผลในการปฏิเสธ');
      return;
    }

    setIsSubmitting(true);
    try {
      await onComplete({
        negotiationId: item.negotiationId,
        status: 'rejected',
        remarks: rejectReason
      }); 
      setShowRejectDialog(false);
      setRejectReason('');
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={style}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
    >
      <Paper>
        <Box p={3}>
          {!item ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <CircularProgress />
              <Typography variant="h6" color="text.secondary">
                กำลังโหลดข้อมูล...
              </Typography>
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Chip 
                  label="ต่อราคาสินค้า" 
                  color="secondary" 
                  sx={{ 
                    fontSize: '1.1rem',
                    height: '32px',
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  รายละเอียดการต่อราคา
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />

              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  URL สินค้า
                </Typography>
                <Link 
                  href={item.itemUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ 
                    wordBreak: 'break-all',
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {item.itemUrl}
                </Link>
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  ราคาที่ต้องการต่อรอง
                </Typography>
                <Typography variant="h6" color="secondary.main">
                  ¥{item.proposedPrice?.toLocaleString()}
                </Typography>
                {currentPrice > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    ราคาปัจจุบัน ¥{currentPrice.toLocaleString()}
                    {discountPercent != null && ` · ขอลด ${discountPercent}%`}
                    {item.maxDiscountPercent != null && ` (เพดาน ${item.maxDiscountPercent}%)`}
                  </Typography>
                )}
                {overCap && (
                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                    ขอลดเกินเพดานที่กำหนด — ควรปฏิเสธหรือตกลงที่ราคาอื่น
                  </Typography>
                )}
              </Box>

              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    ข้อความสำหรับส่งผู้ขาย
                  </Typography>
                  {sellerMessage && (
                    <IconButton
                      onClick={handleCopyPrice}
                      size="small"
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {sellerMessage && (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', p: 1.5, mt: 0.5, bgcolor: 'grey.100', borderRadius: 1 }}
                  >
                    {sellerMessage}
                  </Typography>
                )}
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  ข้อมูลลูกค้า
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body1" gutterBottom>
                    ชื่อ: {item.nickname || 'N/A'}
                  </Typography>
                  <Typography variant="body1">
                    เบอร์โทร: {item.phone || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {confirmAccept && (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>คอมเม้นต์จากบัญชีของใคร (บอทจะส่งงานให้คนนี้เท่านั้น)</InputLabel>
                    <Select
                      value={buyerId}
                      onChange={(e) => setBuyerId(e.target.value)}
                      label="คอมเม้นต์จากบัญชีของใคร (บอทจะส่งงานให้คนนี้เท่านั้น)"
                    >
                      <MenuItem value="">ไม่ระบุ — ส่งงานตามคิวปกติ</MenuItem>
                      {buyers.map((b) => (
                        <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" sx={{ mb: 1.5, textAlign: 'right' }}>
                    ยืนยันว่าส่งคำขอต่อราคา ¥{item.proposedPrice?.toLocaleString()} ให้ผู้ขายแล้ว?
                  </Typography>
                </>
              )}
              <Box display="flex" justifyContent="flex-end" gap={2}>
                {confirmAccept ? (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleComplete}
                      disabled={isSubmitting}
                      sx={{ minWidth: '100px' }}
                    >
                      {isSubmitting ? <CircularProgress size={24} /> : 'ส่งคำขอแล้ว'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setConfirmAccept(false)}
                      disabled={isSubmitting}
                      sx={{ minWidth: '100px' }}
                    >
                      ยังไม่ยืนยัน
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setConfirmAccept(true)}
                    disabled={isSubmitting}
                    sx={{ minWidth: '100px' }}
                  >
                    เสร็จสิ้น
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={isSubmitting || confirmAccept}
                  sx={{ minWidth: '100px' }}
                >
                  ปฏิเสธ
                </Button>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={isSubmitting}
                  sx={{ minWidth: '100px' }}
                >
                  ปิด
                </Button>                
              </Box>
            </>
          )}
        </Box>
      </Paper>
      <Modal
        isOpen={showRejectDialog}
        onRequestClose={() => setShowRejectDialog(false)}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
          },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '400px',
            maxWidth: '90%',
            padding: '20px',
            borderRadius: '8px'
          }
        }}
        ariaHideApp={false}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            เลือกเหตุผลในการปฏิเสธ
          </Typography>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>เหตุผล</InputLabel>
            <Select
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              label="เหตุผล"
            >
              <MenuItem value="seller_no_discount">คนขายตั้งไว้ว่าไม่ลดราคา</MenuItem>
              <MenuItem value="exclusive_item">เป็นสินค้าเฉพาะของลูกค้าท่านอื่น</MenuItem>
              <MenuItem value="sold_out">สินค้าถูกขายไปแล้ว</MenuItem>
              <MenuItem value="prohibited_item">สินค้าต้องห้าม / นำเข้าไม่ได้</MenuItem>
            </Select>
          </FormControl>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={!rejectReason || isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'ยืนยันการปฏิเสธ'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowRejectDialog(false)}
              disabled={isSubmitting}
            >
              ยกเลิก
            </Button>
          </Box>
        </Box>
      </Modal>
    </Modal>
  );
};

export default PriceNegotiationModal;